<template>
  <div class="tracker-selection p-2">
    <b-row>
      <b-col>
        <!-- <p><b>Please choose the trackers you would like to attach to this placement.</b></p> -->
        <!-- <p class="m-0">
          {{ selectedItems.length }} trackers selected for validation
        </p> -->
      </b-col>
      <b-col class="d-flex align-items-center justify-content-end mb-2">
        <b-form-group
          class="d-flex align-items-center m-0"
          label-class="text-right m-0"
          label-for="referenceColumn"
        >
          <template
            slot="label"
          >
            Reference Column
            <i
              class="fa-light fa-info-circle"
              v-b-tooltip.hover
              title="This column will be used to assign trackers to the
               selected content item feed"
            />
          </template>
          <b-form-select
            id="referenceColumn"
            class="ml-2"
            data-test="reference-column"
            :options="headers"
            v-model="referenceColumnData"
          />
        </b-form-group>
        <b-dropdown
          id="ddown1"
          text="Filter columns"
          class="ml-5 d-block"
          right
        >
          <div class="dropdown-item">
            <div class="custom-control custom-checkbox custom-control-inline">
              <input
                class="custom-control-input"
                type="checkbox"
                id="select-all"
                v-model="selectAllColumns"
                data-test="select-all-columns"
                name="columnsFilter"
                @change="onSelectAllChange"
                :value="true"
              >
              <label
                for="select-all"
                class="custom-control-label"
              >
                Select All
              </label>
            </div>
          </div>
          <div
            class="dropdown-item"
            v-for="(field, key) in headers"
            :key="key"
          >
            <div class="custom-control custom-checkbox custom-control-inline">
              <input
                class="custom-control-input"
                type="checkbox"
                :id="`columns${key}`"
                v-model="displayColumns"
                :data-test="`select-column-${key}`"
                name="columnsFilter"
                @change="onFilterColumnSelected"
                :value="field"
                :checked="displayColumns.includes(field)"
              >
              <label
                :for="`columns${key}`"
                class="custom-control-label"
              >
                {{ field }}
              </label>
            </div>
          </div>
        </b-dropdown>
      </b-col>
    </b-row>

    <div
      v-if="filteredColumns.length > 1 && displayedTrackerItems.length > 0"
      class="tracker-selection-con"
    >
      <b-table
        class="card-table trackers-list"
        responsive
        hover
        selectable
        select-mode="single"
        sticky-header="55vh"
        :fields="filteredColumns"
        :items="displayedTrackerItems"
        data-test="trackers-list"
        @row-selected="onRowSelected"
      >
        <template v-slot:table-colgroup="scope">
          <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="{ width: field.width ? field.width : 'auto' }"
          >
        </template>
        <template
          v-slot:cell(combination)="data"
        >
          <div class="combination-select">
            <div
              v-if="data.item.uuid === selectedRow.uuid"
              class="d-flex align-items-center"
            >
              <b-form-select
                v-if="combinationSplit.size"
                :value="data.item.creativeIndex"
                :options="getCombinations.creatives"
                data-test="creative-selection"
                class="combination-selection"
                @change="onCombinationSelected('creativeIndex', $event, data.item)"
              />
              <b-form-select
                v-if="combinationSplit.publisher"
                :value="data.item.publisherIndex"
                :options="getCombinations.publishers"
                data-test="publisher-selection"
                class="combination-selection"
                @change="onCombinationSelected('publisherIndex', $event, data.item)"
              />
              <b-form-select
                v-if="combinationSplit.geo"
                :value="data.item.geoIndex"
                :options="getCombinations.geos"
                data-test="geo-selection"
                class="combination-selection"
                @change="onCombinationSelected('geoIndex', $event, data.item)"
              />
              <div
                class="d-flex ml-2 cursor-pointer"
                @click="onDeleteTracker(data.item)"
              >
                <i class="fal fa-trash-alt m-auto px-2" />
              </div>
            </div>
            <div
              v-else-if="hasCombination(data.item)"
              class="d-flex align-items-center justify-content-center"
            >
              <span :title="getCombinationTextFromItem(data.item)">
                {{ getCombinationTextFromItem(data.item) }}
              </span>
            </div>
            <div
              v-else
              data-test="select-combination"
              class="d-flex align-items-center justify-content-center"
            >
              <span>click here to change combination</span>
            </div>
          </div>
        </template>
      </b-table>
      <BasePagination
        class="pagination-edit"
        v-if="totalItems > perPageCount"
        :items-count="totalItems"
        :current-page.sync="currentPage"
        :per-page-selected.sync="perPageCount"
        items-name="Items"
        :hide-page-select="false"
      />
    </div>

    <b-alert
      variant="danger"
      v-else-if="filteredColumns.length <= 1"
      show
    >
      Please choose one or more columns to display
    </b-alert>
    <div
      class="loading"
      v-else
    >
      <div class="loading-con">
        <span>Loading...</span>
        <b-spinner variant="primary" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapMutations,
  mapGetters,
  mapState,
} from 'vuex';
import * as trackersTypes from '@/modules/trackers/store/types';

import selectItemTable from '@/core/mixins/selectItemTable';

export default {
  name: 'TrackersSelection',

  mixins: [selectItemTable],

  props: {
    items: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    referenceColumn: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      selectAllColumns: true,
      selectedRow: {},
      currentPage: 1,
      perPageCount: 20,
    };
  },

  methods: {
    ...mapMutations('trackers', {
      removeTracker: trackersTypes.REMOVE_TRACKER,
      updateTrackerCombination: trackersTypes.UPDATE_TRACKER_COMBINATION,
      updateTrackersSelectedCombinations: trackersTypes.UPDATE_TRACKERS_SELECTED_COMBINATIONS,
      setColumnsToDisplay: trackersTypes.SET_COLUMNS_TO_DISPLAY,
    }),

    hasCombination(item) {
      const hasCreativeIndex = item.creativeIndex != null;
      const hasPublisherIndex = item.publisherIndex != null;
      const hasGeoIndex = item.geoIndex != null;
      return hasCreativeIndex || hasPublisherIndex || hasGeoIndex;
    },

    onRowSelected(rowsSelected) {
      if (rowsSelected == null || rowsSelected.length === 0) return;
      this.selectedRow = rowsSelected[0];
    },

    getCombinationTextFromItem(item) {
      if (!item) return;
      const combinations = this.getCombinations;
      function getTextFromIndex(type, index) {
        if (index !== null) return combinations[type][index].text;
        return 'ALL';
      }
      return `${getTextFromIndex('creatives', item?.creativeIndex)} - ${getTextFromIndex('publishers', item?.publisherIndex)} - ${getTextFromIndex('geos', item?.geoIndex)}`;
    },

    onSelect(item, index) {
      this.onRowClick(item, index);
      this.sendChangeEvent();
    },
    onSelectAllChange() {
      if (!this.selectAllColumns) this.displayColumns = [this.referenceColumn];
      else this.displayColumns = this.headers;
    },
    onFilterColumnSelected() {
      if (!this.isDisabled) this.checkTrackersForCombinations();
      else this.$emit('disable-next', this.isDisabled);
    },

    sendChangeEvent() {
      this.$emit('disable-next', this.isDisabled);
      /* if (this.referenceColumn) {
        this.$emit('send-tracker-data', this.referenceColumn, this.selectedItems);
      } */
    },

    checkTrackersForCombinations() {
      if (this.trackersSelectedCombinations.size > 0) this.$emit('disable-next', false);
      else this.$emit('disable-next', true);
    },

    updateTrackerCombinations() {
      if (this.combinationSplit.size) {
        this.items.filter((item) => item.creativeId).forEach((item) => this.updateCreativeIndex(item));
      }
      if (this.combinationSplit.publisher) {
        this.items.filter((item) => item.publisherId).forEach((item) => this.updatePublisherIndex(item));
      }
      if (this.combinationSplit.geo) {
        this.items.filter((item) => item.geoGroupIds.length > 0 || item.countryIds.length > 0)
          .forEach((item) => this.updateGeoIndex(item));
      }
    },

    updateCreativeIndex(trackerCombination) {
      const creativeTemplateId = this.getTemplateByCreativeId(trackerCombination.creativeId)?.id;
      const creativeIndex = this.getCombinations.creatives
        .findIndex((creativeCombination) => creativeCombination.id === creativeTemplateId);
      this.updateIndex(creativeIndex, 'creativeIndex', trackerCombination);
    },

    updatePublisherIndex(trackerCombination) {
      const publisherIndex = this.getCombinations.publishers
        .findIndex((publisherCombination) => publisherCombination.id === trackerCombination.publisherId);
      this.updateIndex(publisherIndex, 'publisherIndex', trackerCombination);
    },

    updateGeoIndex(trackerCombination) {
      const geoId = trackerCombination.countryIds.length > 0 ? trackerCombination.countryIds[0] : trackerCombination.geoGroupIds[0];
      const type = trackerCombination.countryIds.length > 0 ? 'country' : 'region';
      const geoIndex = this.getCombinations.geos
        .findIndex((geoCombination) => geoCombination.id === geoId && geoCombination.type === type);
      this.updateIndex(geoIndex, 'geoIndex', trackerCombination);
    },

    updateIndex(newIndex, propName, trackerCombination) {
      if (newIndex > -1) {
        trackerCombination[propName] = newIndex;
        this.onCombinationSelected(propName, newIndex, trackerCombination);
      }
    },

    onDeleteTracker(item) {
      this.removeTracker(item);
      const selectedCombinations = this.trackersSelectedCombinations;
      if (selectedCombinations.has(item.uuid)) selectedCombinations.delete(item.uuid);
      this.updateTrackersSelectedCombinations(selectedCombinations);

      this.checkTrackersForCombinations();
    },

    onCombinationSelected(prop, $event, item) {
      const newItem = { ...item, [prop]: $event };
      this.updateTrackerCombination(newItem);

      const selectedCombinations = this.trackersSelectedCombinations;
      if (!selectedCombinations.has(newItem.uuid)) selectedCombinations.set(newItem.uuid, true);
      this.updateTrackersSelectedCombinations(selectedCombinations);

      this.checkTrackersForCombinations();
    },
  },
  computed: {
    ...mapGetters('trackers', {
      getCombinations: trackersTypes.GET_ALL_COMBINATIONS,
      getTrackers: trackersTypes.GET_PARSED_FILE_RESULT_WITH_COMBINATIONS,
      getTemplateByCreativeId: trackersTypes.GET_TEMPLATE_BY_CREATIVE_ID,
    }),
    ...mapState('trackers', [
      'trackersSelectedCombinations',
      'combinationSplit',
      'columnsToDisplay',
    ]),

    displayColumns: {
      get() {
        return this.columnsToDisplay;
      },
      set(val) {
        this.setColumnsToDisplay(val);
      },
    },

    referenceColumnData: {
      get() {
        return this.referenceColumn;
      },
      set(val) {
        this.$emit('set-reference-column', val);
      },
    },

    displayedTrackerItems() {
      const startingIndex = this.currentPage === 1 ? 0 : ((this.currentPage - 1) * this.perPageCount);
      const endingIndex = this.currentPage * this.perPageCount;
      return this.getTrackers.slice(startingIndex, endingIndex);
    },
    totalItems() {
      return this.getTrackers.length;
    },

    filteredColumns() {
      const filteredList = this.fields.filter((field) => this.columnsToDisplay.includes(field.label));
      return [...filteredList, 'combination'];
    },

    isDisabled() {
      return (
        this.columnsToDisplay.length === 1
        || !this.referenceColumn
      );
    },

    fields() {
      const fields = this.headers.map((header, index) => ({
        key: `row[${index}]`,
        label: header,
        sortable: true,
      }));

      return [
        ...fields,
        {
          key: 'combination',
          label: 'tracker combination',
          width: '400px',
          stickyColumn: true,
        },
      ];
    },
  },
  watch: {
    displayedTrackerItems(newVal) {
      if (newVal.length === 0) this.currentPage = 1;
    },
  },

  created() {
    this.checkTrackersForCombinations();
    this.updateTrackerCombinations();
    if (this.displayColumns.length <= 0) this.displayColumns = this.fields.map((field) => field.label);
  },
};
</script>
<style lang="scss" scoped>
.tracker-selection {
  max-height: 60vh;
}
.combination-select {
  width: 400px;
  select {
    width: 100%;
    margin: 0 5px;
  }
}
.pagination-edit {
  margin: 10px 105px 0 105px;
}
.loading, .loading-con {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}
.loading-con span {
  font-size: 20px;
  font-weight: 600;
  margin-right: 0.75rem
}
</style>
<style lang="css">
.trackers-list th {
  white-space: nowrap;
}
.trackers-list td {
  word-break: normal;
  font-size: 12px;
  white-space: nowrap;
  vertical-align: inherit;
}
.b-table-sticky-column{
  right: 0;
}
.combination-selection{
  width: 20vw;
}
</style>
